import Box from 'components/shared/Box'
import { Col, Container, Row } from 'styled-bootstrap-grid'
import Header from 'components/global/Header'
import Summary from 'components/global/Summary'
// import BenefitsInfo from 'components/global/BenefitsInfo'
// import BlackWeekendBenefitsInfo from 'components/global/promotions/BlackWeekendBenefitsInfo'
import UnlimitedBenefitsInfo from 'routes/unlimited-card/UnlimitedBenefitsInfo'
import { Content, PageWrapper } from './styles'
import { useResolutions } from 'store/resolutions'
import { useCart } from 'store/cart-slice/hooks'
import { useApp } from 'store/steps'
import SelfcareSummary from 'components/global/SelfcareSummary'
import SelfcareInfo from 'components/global/SelfcareInfo'
import {
    SelfcareStep,
    Step,
    UnlimitedCardStep,
    UnlimitedRenewStep,
    VouchersStep,
} from 'store/general-slice/types'
import { ClearNextStepsModal } from 'components/global/ClearNextStepsModal'
import { useAppSelector } from 'store/hooks'
import ProductList from 'components/global/Summary/sections/ProductList'
import CardsProductList from 'components/global/Summary/sections/CardsProductList'
import UnlimitedCta from 'components/global/UnlimitedCta'
import AccommodationInfoAside from 'components/global/AccommodationInfoAside'
import UnlimitedBlackCta from 'components/global/UnlimitedBlackCta'
import WinterBreakAttractionsAsideInfo from 'components/shared/Promotions/components/WinterBreakAttractionsAsideInfo'
import { usePromotions } from 'components/shared/Promotions/utils/usePromotions'
import UnlimitedBirthdayPromotionCta from 'components/global/promotions/BirthdayPromotion/UnlimitedBirthdayPromotionCta'
import VouchersProductList from 'components/global/Summary/sections/VouchersProductList'

interface PageProps {
    isFullWidth?: boolean
    children: React.ReactNode
}

const Page = ({ isFullWidth, children }: PageProps) => {
    const { isTablet } = useResolutions()
    const { isCartEmpty, isSummaryFetching } = useCart()
    const { stepId, isCreatorAccommodation, isUnlimitedMode } = useApp()
    const {
        isBlackPromoForUnlimitedVisibleOutside,
        isBirthdayPromoForTickets,
        isBirthdayPromoForUnlimitedVisibleOutside,
        isWinterBreakPromo,
    } = usePromotions()
    const { nextStepToEdit, appMode, isAppAccessible } = useAppSelector(
        (state) => state.general
    )
    const { countChanges, countNewItems } = useAppSelector(
        (state) => state.selfcareCart
    )

    const creatorSummary = (() => {
        const isOneOfAccommodationSteps = stepId?.includes('accommodation')

        // cart empty - show some teasers
        if (isCartEmpty && !isSummaryFetching) {
            if (isBirthdayPromoForUnlimitedVisibleOutside)
                return <UnlimitedBirthdayPromotionCta />
            if (isBlackPromoForUnlimitedVisibleOutside)
                return <UnlimitedBlackCta />
            if (isCreatorAccommodation && isOneOfAccommodationSteps)
                return <AccommodationInfoAside />
            if (isWinterBreakPromo) return <WinterBreakAttractionsAsideInfo />
            return <UnlimitedCta />
        }
        // cart full - list items
        return <Summary productList={<ProductList />} />
    })()

    const selfcareSummary =
        countChanges > 0 || countNewItems > 0 ? (
            <SelfcareSummary />
        ) : (
            <SelfcareInfo />
        )

    const unlimitedCardSummary = (() => {
        if (
            (isCartEmpty && !isSummaryFetching) ||
            stepId === 'card_configuration'
        )
            return <UnlimitedBenefitsInfo />
        return <Summary productList={<CardsProductList />} />
    })()

    const unlimitedRenewSummary = (() => {
        if (!isCartEmpty && stepId !== 'renew_verification')
            return <Summary productList={<CardsProductList />} />
        else return <></>
    })()

    const vouchersSummary = (() => {
        if (!isCartEmpty && stepId !== 'vouchers_configuration')
            return <Summary productList={<VouchersProductList />} />
        else return <></>
    })()

    const notFullWidthPages: (
        | Step
        | SelfcareStep
        | UnlimitedCardStep
        | UnlimitedRenewStep
        | VouchersStep
    )[] = [
        'tickets',
        'upsell',
        'accommodation',
        'accommodation_details',
        'order',
        'edit_order',
        'summary',
        'selfcare_order',
        'card_configuration',
        'card_summary',
        'card_order',
        'renew_verification',
        'renew_summary',
        'renew_order',
        'vouchers_configuration',
        'vouchers_summary',
        'vouchers_order',
    ]

    const isWithSummary =
        !isFullWidth && notFullWidthPages.some((id) => id === stepId)

    const hasPalmBackground = isWithSummary
    const hasParkBackground = appMode === 'error' || stepId === 'payment_failed'
    const hasPalmReversedBackground =
        'registration_confirmation' === stepId || stepId === 'payment'

    let backgroundDecor: 'palm' | 'park' | 'palmReversed' | undefined =
        undefined

    const isVouchersMode = appMode === 'vouchers'

    if (!isAppAccessible || hasParkBackground) {
        backgroundDecor = 'park'
    } else if (hasPalmBackground) {
        backgroundDecor = 'palm'
    } else if (hasPalmReversedBackground) {
        backgroundDecor = 'palmReversed'
    }

    let summary = creatorSummary

    switch (appMode) {
        case 'selfcare':
            summary = selfcareSummary
            break
        case 'unlimited-card':
            summary = unlimitedCardSummary
            break
        case 'unlimited-renew':
            summary = unlimitedRenewSummary
            break
        case 'vouchers':
            summary = vouchersSummary
            break
    }

    return (
        <PageWrapper>
            {stepId !== 'thankyou' && <Header />}
            <Content
                backgroundDecor={
                    isBirthdayPromoForTickets ? 'birthday' : backgroundDecor
                }
            >
                <Box
                    pt={[28, !isUnlimitedMode && !isVouchersMode ? 56 : 18]}
                    pb={120}
                >
                    <Container>
                        <Row>
                            {isWithSummary && isAppAccessible ? (
                                <>
                                    <Col
                                        md={10}
                                        mdOffset={1}
                                        lg={8}
                                        lgOffset={2}
                                        xlOffset={0}
                                        xl={9}
                                    >
                                        {children}
                                    </Col>
                                    {!isTablet && <Col md={3}>{summary}</Col>}
                                </>
                            ) : (
                                <Col>{children}</Col>
                            )}
                        </Row>
                    </Container>
                </Box>
            </Content>
            {nextStepToEdit && (
                <ClearNextStepsModal
                    nextStep={nextStepToEdit}
                    isOpened={!!nextStepToEdit}
                />
            )}
        </PageWrapper>
    )
}

export default Page
